import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

/**
 * Score Big - Displays player score, upgrades, and level during gameplay
 *
 * Shows in the top-right of the game screen with:
 * - Bounty/score (main large number)
 * - Upgrade count
 * - Level/rank
 *
 * On hover (hover-capable devices only), shows a "Lifetime Stats" tooltip with:
 * - Kills and Deaths
 * - K/D Ratio and Level
 * - XP and Next Level requirement
 * - Sign in prompt (if not logged in)
 *
 * Not rendered on small screens (phones, either orientation) — upgrade
 * availability shows via the upgrade-selector glow, bounty via the
 * scoreboard.
 *
 * @element score-big
 */
@customElement('score-big')
export class ScoreBig extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
        position: absolute;
        /* + the status-bar inset, in step with the toolbar above it (see
           airmash-game). Hidden on phones; a tablet's installed app has one. */
        top: calc(54px + env(safe-area-inset-top, 0px));
        right: 50px;
        width: 200px;
        height: 120px;
        text-align: right;
        color: var(--color-text-white);
        cursor: default;
        white-space: nowrap;
        z-index: 1;
        /* Declared here rather than inherited from the body. The card can be
           hovered, so no text selection either. */
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        /* The box is mostly empty space around the chips, so it lets pointers
           through: a mouse-mode click there fires (mouse fire needs the canvas
           as its target), and the card stays shut. */
        pointer-events: none;
      }

      /* Hover devices: the chips take the pointer, and hovering one opens the
         card. While it's open the whole box holds it open, so the pointer can
         cross the empty space to SIGN IN. On touch nothing opts in: the card
         needs a hovering pointer, and taps fall through to the game. */
      @media (hover: hover) {
        .score-value,
        .stat-value,
        :host(:hover) {
          pointer-events: auto;
        }
      }

      /* Touch toolbar chips are 44px tall (menu-toolbar's hover:none block),
         so the toolbar ends at 58px and BOUNTY sat against it. Drop to the row
         below, with the ping readout and the chat button. In practice that's
         tablets: phones don't render the block. */
      @media (hover: none) {
        :host {
          top: calc(64px + env(safe-area-inset-top, 0px));
        }
      }

      /* Main score display */
      .bounty-label {
        color: var(--color-text-tooltip);
        font-weight: bold;
        font-size: var(--font-size-xs);
        width: 200px;
        text-align: center;
        position: absolute;
        top: 0;
        right: 0;
        text-shadow: var(--shadow-text);
      }

      .container-score {
        font-weight: 800;
        font-size: 36px;
        text-align: center;
        position: absolute;
        width: 200px;
        top: 16px;
        right: 0;
      }

      .score-value {
        display: inline-block;
        background-color: var(--color-bg-chip);
        border-radius: var(--radius-lg);
        padding: 0 10px 1px 10px;
      }

      /* Small stat labels */
      .stat-label {
        position: absolute;
        width: 40px;
        top: 66px;
        text-align: center;
        color: var(--color-text-tooltip);
        font-weight: bold;
        font-size: var(--font-size-xs);
        text-shadow: var(--shadow-text);
      }

      .stat-label.upgrades {
        right: 102px;
      }

      .stat-label.level {
        right: 58px;
      }

      /* Small stat values */
      .container-stat {
        text-align: center;
        position: absolute;
        width: 40px;
        top: 82px;
        font-weight: bold;
        font-size: 19px;
      }

      .container-stat.upgrades {
        right: 102px;
      }

      .container-stat.level {
        right: 58px;
      }

      .stat-value {
        display: inline-block;
        background-color: var(--color-bg-chip);
        border-radius: var(--radius-md);
        padding: 2px 8px;
      }

      /* Hover tooltip */
      .detailed-bounty {
        display: none;
        position: absolute;
        right: 190px;
        top: -20px;
        width: 200px;
        background-color: var(--color-bg-panel);
        border-radius: var(--radius-lg);
        border-bottom: 2px solid var(--color-border-strong);
        border-top: 2px solid var(--color-border-light);
        padding: 10px 10px 14px 10px;
        text-align: left;
      }

      /* Hover-only: on touch, :host(:hover) would stick after a tap and
         pin the tooltip open until the user taps elsewhere. */
      @media (hover: hover) {
        :host(:hover) .detailed-bounty {
          display: block;
        }
      }

      .detailed-bounty .arrow {
        position: absolute;
        top: 46px;
        right: -10px;
        width: 0;
        height: 0;
        border-top: 10px solid transparent;
        border-bottom: 10px solid transparent;
        border-left: 10px solid var(--color-bg-panel);
      }

      .detailed-bounty .header {
        font-weight: bold;
        font-size: var(--font-size-lg);
        color: var(--color-text-white);
        text-align: center;
        padding-top: 6px;
        padding-bottom: 6px;
      }

      .detailed-bounty .item {
        display: flex;
        justify-content: space-around;
      }

      .detailed-bounty .item .top {
        width: 100px;
        text-align: center;
        color: var(--color-text-tooltip);
        margin-top: 10px;
      }

      .detailed-bounty .item .bottom {
        width: 100px;
        text-align: center;
        color: var(--color-text-white);
        font-size: var(--font-size-lg);
        font-weight: bold;
        margin-top: 2px;
      }

      /* Account section */
      .account {
        text-align: center;
      }

      .account .not-logged-in {
        color: var(--color-text-tertiary);
        margin-top: 16px;
        margin-bottom: 10px;
      }

      .account .sign-in-btn {
        display: inline-block;
        vertical-align: middle;
        padding: 2px 6px;
        background-color: var(--color-btn-login);
        border-radius: var(--radius-sm);
        font-size: var(--font-size-xs);
        font-weight: bold;
        cursor: pointer;
        color: var(--color-text-white);
        text-shadow: var(--shadow-text-btn);
        border-bottom: 2px solid var(--color-border-soft);
      }

      .account .sign-in-btn:hover {
        background-color: var(--color-btn-login-hover);
        box-shadow: var(--shadow-btn-hover);
      }

      .account .sign-in-btn:active {
        border-bottom: none;
        border-top: 2px solid var(--color-border-strong);
      }

      /* Phones and other cramped viewports: not rendered at all. The block
         is 200×120 of secondary info — upgrade availability is already
         signalled by the upgrade-selector glow, and bounty lives in the
         scoreboard. (A consolidated expandable mobile HUD is a parked
         idea; until then absence beats a cryptic chip.) The max-height arm
         catches landscape phones, where width alone wouldn't. */
      @media (max-width: 700px), (max-height: 500px) {
        :host {
          display: none;
        }
      }
    `,
  ];

  /** UI state from context */
  private uiState = bindValtio(this, uiStateProxy);

  /** Game API from context */
  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  override render() {
    const { score, level, availableUpgrades, lifetimeStats: rawStats, isLoggedIn } = this.uiState;
    const lifetimeStats = rawStats ?? { kills: 0, deaths: 0, earnings: 0, nextLevelXp: 0 };

    // Calculate K/D ratio
    const kdRatio =
      lifetimeStats.kills === 0 || lifetimeStats.deaths === 0
        ? '-'
        : (lifetimeStats.kills / lifetimeStats.deaths).toFixed(2);

    // Format next level XP
    const nextLevelDisplay = lifetimeStats.nextLevelXp > 0 ? `+${lifetimeStats.nextLevelXp}` : '-';

    return html`
      <!-- Main score display -->
      <div class="bounty-label">BOUNTY</div>
      <div class="container-score">
        <span class="score-value">${score}</span>
      </div>

      <div class="stat-label upgrades">UPG</div>
      <div class="container-stat upgrades">
        <span class="stat-value">${availableUpgrades}</span>
      </div>

      <div class="stat-label level">LVL</div>
      <div class="container-stat level">
        <span class="stat-value">${isLoggedIn ? level : '-'}</span>
      </div>

      <!-- Hover tooltip -->
      <div class="detailed-bounty">
        <div class="arrow"></div>
        <div class="header">Lifetime Stats</div>

        <div class="item">
          <div class="top">Kills</div>
          <div class="top">Deaths</div>
        </div>
        <div class="item">
          <div class="bottom">${lifetimeStats.kills}</div>
          <div class="bottom">${lifetimeStats.deaths}</div>
        </div>

        <div class="item">
          <div class="top">K/D Ratio</div>
          <div class="top">Level</div>
        </div>
        <div class="item">
          <div class="bottom">${kdRatio}</div>
          <div class="bottom">${isLoggedIn ? level : '-'}</div>
        </div>

        <div class="item">
          <div class="top">XP</div>
          <div class="top">Next Level</div>
        </div>
        <div class="item">
          <div class="bottom">${isLoggedIn ? lifetimeStats.earnings : '-'}</div>
          <div class="bottom">${isLoggedIn ? nextLevelDisplay : '-'}</div>
        </div>

        ${
          !isLoggedIn
            ? html`
                <div class="account">
                  <div class="not-logged-in">Sign in to save your stats</div>
                  <div class="sign-in-btn" @click=${this.handleSignIn}>SIGN IN</div>
                </div>
              `
            : nothing
        }
      </div>
    `;
  }

  /** Handle sign in button click - redirects to lobby */
  private handleSignIn(e: Event): void {
    e.stopPropagation();
    this.gameApi.playClick();
    this.gameApi.changeRegion();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'score-big': ScoreBig;
  }
}
